import React, { useState, useEffect, useRef } from 'react';
import {
  Text,
  TouchableOpacity,
  Modal,
  View,
  Platform,
  TextStyle,
  ViewStyle,
  Linking,
} from 'react-native';
import { FontAwesome5 } from '@expo/vector-icons';
import QRCode from 'react-native-qrcode-svg';
import { captureRef } from 'react-native-view-shot';
import * as Sharing from 'expo-sharing';

import { getBrandsData } from '../../../../api/Price';
import BasicModalContainer from '../../../../common/components/basicmodal/BasicModalContainer';
import ModalHeader from '../../../../common/components/basicmodal/ModalHeader';
import ModalBody from '../../../../common/components/basicmodal/ModalBody';
import ModalFooter from '../../../../common/components/basicmodal/ModalFooter';
import { useAlertModal } from '../../../../common/hooks';

const ProductQRModal = ({
  isModalVisible,
  product,
  closeModal,
}) => {
  const { showAlert } = useAlertModal();
  const [brands, setBrands] = useState([]);
  const [qrLink, setQrLink] = useState('');
  const qrCodeRef = useRef();

  useEffect(() => {
    if (isModalVisible) {
        getBrandsData((jsonRes) => {
            setBrands(jsonRes);
        });
    } else {
        setBrands([]);
        setQrLink('');
    }
  }, [isModalVisible]);

  useEffect(() => {
    if (product?.barcode && brands.length > 0) {
      const brand = brands.find((b: any) => b.storedetail && b.storedetail.store_url);
      if (brand && brand.storedetail && brand.storedetail.store_url) {
        // Using barcode param for product QR codes
        const url = `https://quickorder.bodhisys.io/qr?code=${product.qr_hash}`;
        setQrLink(url);
      }
    }
  }, [product, brands]);

  const handleDownloadQR = async () => {
    try {
      const uri = await captureRef(qrCodeRef, {
        format: 'png',
        quality: 1,
      });

      if (Platform.OS === 'web') {
        const response = await fetch(uri);
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        // Filename based on barcode
        link.download = `qrcode-${product?.qr_hash || 'product'}.png`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
      } else {
        await Sharing.shareAsync(uri);
      }
    } catch (error) {
      console.error('Error capturing QR code:', error);
      showAlert('error', 'Failed to capture QR code');
    }
  };

  return (
    <Modal
      animationType="none"
      transparent={true}
      visible={isModalVisible}
      onRequestClose={closeModal}
    >
      <BasicModalContainer>
        <ModalHeader label={'Product QR Code'} closeModal={closeModal} />
        <ModalBody>
            <View style={{ alignItems: 'center', padding: 20 }}>
                {qrLink ? (
                  <View style={styles.qrSectionContainer}>
                    <View style={styles.qrCodeWrapper}>
                      <View ref={qrCodeRef} collapsable={false} style={styles.qrCodeContainer}>
                        <QRCode
                          value={qrLink}
                          size={200}
                          backgroundColor="white"
                        />
                      </View>
                    </View>
                    <View style={styles.qrActions}>
                      <TouchableOpacity
                        style={[styles.qrActionButton, { backgroundColor: '#2196F3' }]}
                        onPress={handleDownloadQR}
                      >
                        <FontAwesome5 name="download" size={14} color="white" />
                        <Text style={styles.qrActionButtonText}>Download</Text>
                      </TouchableOpacity>
                      <TouchableOpacity
                        style={[styles.qrActionButton, { backgroundColor: '#6c757d' }]}
                        onPress={() => Linking.openURL(qrLink)}
                      >
                        <FontAwesome5 name="external-link-alt" size={14} color="white" />
                        <Text style={styles.qrActionButtonText}>Open Link</Text>
                      </TouchableOpacity>
                    </View>
                  </View>
                ) : (
                    <View style={{ padding: 20 }}>
                        <Text>{product?.qr_hash ? 'Loading QR Code...' : 'No barcode found for this product.'}</Text>
                    </View>
                )}
            </View>
        </ModalBody>
        <ModalFooter>
          <TouchableOpacity onPress={closeModal}>
            <Text style={styles.closeButton}>Close</Text>
          </TouchableOpacity>
        </ModalFooter>
      </BasicModalContainer>
    </Modal>
  );
};

const styles = {
    qrSectionContainer: {
        borderWidth: 1,
        borderColor: '#ccc',
        borderRadius: 5,
        padding: 10,
        backgroundColor: '#f8f9fa',
        width: '100%' as any,
        alignItems: 'center' as ViewStyle['alignItems'],
      },
      qrCodeWrapper: {
        alignItems: 'center' as ViewStyle['alignItems'],
        marginVertical: 10,
      },
      qrCodeContainer: {
        padding: 10,
        backgroundColor: 'white',
        borderRadius: 5,
        borderWidth: 1,
        borderColor: '#eee',
      },
      qrActions: {
        flexDirection: 'row' as ViewStyle['flexDirection'],
        justifyContent: 'center' as ViewStyle['justifyContent'],
        gap: 10,
        marginTop: 10
      },
      qrActionButton: {
        flexDirection: 'row' as ViewStyle['flexDirection'],
        alignItems: 'center' as ViewStyle['alignItems'],
        paddingVertical: 8,
        paddingHorizontal: 15,
        borderRadius: 5,
        gap: 8,
      },
      qrActionButtonText: {
        color: 'white',
        fontSize: 12,
        fontWeight: 'bold' as TextStyle['fontWeight'],
      },
      closeButton: {
        backgroundColor: '#6c757d',
        color: 'white',
        paddingVertical: 10,
        paddingHorizontal: 20,
        borderRadius: 5,
        textAlign: 'center' as TextStyle['textAlign'],
        overflow: 'hidden' as 'hidden',
        fontWeight: 'bold' as TextStyle['fontWeight'],
      }
};

export default ProductQRModal;
